import { useMemo } from 'react';
import { ColumnsType } from '@setvi/shared/types';
import { Customer } from 'Services/Mongo/Types';
import {
  ProductColumn,
  ProductDetailsColumnsIds,
  ProductInventory,
  ProductWarehouse,
  StockInfo
} from '@setvi/shared/interfaces';
import { getWarehouseCellContent } from './columns-content';

export const getWarehouseLocation = (warehouse: ProductWarehouse) => {
  if (!warehouse || (!warehouse.city && !warehouse.state))
    return warehouse?.name || '-';

  return `${warehouse.city && warehouse.city}${
    warehouse.city && warehouse.state ? ',' : ''
  } ${warehouse.state && warehouse.state}`;
};

/**
 * A fixed width keeps the column from flex-growing next to Location - the table
 * only grows columns that have no width, so Location alone takes the slack and
 * Stocked stays next to the quantity columns.
 */
const stockedColumn: ProductColumn = {
  id: ProductDetailsColumnsIds.Stocked,
  label: 'Stocked',
  width: 120,
  minWidth: 120
};

/**
 * DEV-797 - the stored catalog config does not carry the Stocked column yet, so
 * it is added right after Location for products that report stockInfo. Remove
 * this fallback once the company config ships the column on its own.
 */
export const withStockedColumn = (
  configColumns: ProductColumn[],
  stockInfo?: StockInfo
) => {
  if (
    !stockInfo ||
    configColumns.some(column => column.id === ProductDetailsColumnsIds.Stocked)
  )
    return configColumns;

  const locationIndex = configColumns.findIndex(
    column => column.id === ProductDetailsColumnsIds.Location
  );

  return [
    ...configColumns.slice(0, locationIndex + 1),
    stockedColumn,
    ...configColumns.slice(locationIndex + 1)
  ];
};

interface useWarehouseColumnsProps {
  selectedCustomer?: Customer;
  configColumns: ProductColumn[];
  stockInfo?: StockInfo;
  mobile?: boolean;
}

export const useWarehouseColumns = ({
  selectedCustomer,
  configColumns = [],
  stockInfo,
  mobile = false
}: useWarehouseColumnsProps) => {
  const warehouseColumns: ColumnsType = useMemo(
    () =>
      withStockedColumn(configColumns, stockInfo).map(column => ({
        ...column,
        cellContent: (row: ProductInventory) =>
          getWarehouseCellContent({
            id: column.id as ProductDetailsColumnsIds,
            row,
            mobile,
            selectedCustomer,
            stockInfo,
            getWarehouseLocation
          })
      })),
    [configColumns, stockInfo, mobile, selectedCustomer]
  );

  return {
    warehouseColumns
  };
};
